import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { shouldSuggestChromeOnAndroidForPush } from '../lib/deviceDetection';
import { MAIN_APP_BELOW_TOP_BAR_CLASS } from '../lib/layoutChrome';
import { AppButton } from './ui/AppButton';
import {
  getBrowserNotificationPermission,
  isWebPushEnabledOnDevice,
  requestWebPushPermissionAndSubscribe,
  setWebPushEnabledOnDevice,
  shouldShowWebPushSetting,
} from '../lib/pushNotification';

const PROMPT_DISMISSED_STORAGE_KEY = 'fdh-web-push-permission-prompt-dismissed';

const isPromptDismissed = (): boolean => {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markPromptDismissed = (): void => {
  try {
    localStorage.setItem(PROMPT_DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // ignore localStorage failures
  }
};

export const WebPushPermissionPrompt = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (shouldSuggestChromeOnAndroidForPush()) {
      setVisible(false);
      return;
    }

    const canShow = shouldShowWebPushSetting();
    const permission = getBrowserNotificationPermission();
    const enabledOnDevice = isWebPushEnabledOnDevice();
    const dismissed = isPromptDismissed();

    const shouldShow = canShow && permission === 'default' && enabledOnDevice && !dismissed;
    setVisible(shouldShow);
  }, []);

  const handleDismiss = async () => {
    markPromptDismissed();
    setVisible(false);
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      await requestWebPushPermissionAndSubscribe();
      markPromptDismissed();
      setVisible(false);
    } catch {
      await setWebPushEnabledOnDevice(false);
      markPromptDismissed();
      setVisible(false);
    } finally {
      setBusy(false);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={`mx-auto w-full max-w-lg shrink-0 px-4 ${MAIN_APP_BELOW_TOP_BAR_CLASS}`}>
      <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('settings.webPushPromptTitle')}</p>
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{t('settings.webPushPromptBody')}</p>
        <div className="mt-3 flex items-center justify-end gap-2">
          <AppButton
            type="button"
            variant="secondary"
            onClick={() => void handleDismiss()}
            disabled={busy}
            className="min-h-9 px-3 py-1.5 text-xs"
          >
            {t('common.cancel')}
          </AppButton>
          <AppButton
            type="button"
            variant="primary"
            onClick={() => void handleEnable()}
            disabled={busy}
            className="min-h-9 px-3 py-1.5 text-xs"
          >
            {busy ? t('common.loading') : t('settings.webPushPromptEnable')}
          </AppButton>
        </div>
      </div>
    </div>
  );
};
